import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import UnescoMarquee from "@/components/UnescoMarquee";
import AboutContent from "@/components/AboutContent";

/**
 * Page "Qui sommes-nous" — Les Arteurs, l'équipe fondatrice + vision.
 * Contenu principal (équipe + vision + premier chantier) délégué à <AboutContent />
 * pour être réutilisable sur /contact.
 */

export default function QuiSommesNousPage() {
  return (
    <main className="pt-24 pb-20" data-testid="qui-sommes-nous-page">
      <UnescoMarquee />

      {/* Hero */}
      <section className="mt-16 md:mt-24 px-6 md:px-12 max-w-[1500px] mx-auto">
        <div className="meta-label text-cinabre">§ Qui sommes-nous ?</div>
        <h1 className="mt-6 font-serif font-light text-6xl md:text-8xl leading-[0.92] tracking-tight">
          Les <em className="italic">Arteurs</em>.
        </h1>
        <div className="mt-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <p className="font-serif text-xl md:text-2xl italic text-fg/85 leading-[1.4]">
              « Un collectif artistique engagé dans la création, la recherche et la transmission. »
            </p>
            <p className="mt-6 text-fg/80 leading-relaxed text-lg">
              Nous développons des projets qui relient les territoires, les récits contemporains et les formes de présence, avec une ambition : <strong>faire de l'art un espace de transformation individuelle et collective.</strong>
            </p>
          </div>
          <div className="md:col-span-5 self-end">
            <div className="border-l-2 border-cinabre pl-6 py-2">
              <div className="meta-label text-cinabre">§ Notre démarche</div>
              <ul className="mt-4 space-y-3 text-fg/85 leading-relaxed">
                <li><strong>Création artistique</strong> — explorer les lieux, les gestes, les voix, les mémoires.</li>
                <li><strong>Coopération & partenariats</strong> — travailler avec les institutions, les acteurs culturels et les territoires.</li>
                <li><strong>Transmission & médiation</strong> — ouvrir l'art, le rendre accessible, partageable, vivant.</li>
              </ul>
            </div>
          </div>
        </div>
        <p className="mt-14 max-w-3xl text-fg/70 leading-relaxed">
          Nous sommes une équipe où les parcours se complètent : artistes, conservateurs, professionnels du patrimoine, experts institutionnels, et voix émergentes. Une équipe qui relie <em className="italic">l'Atlantique à la Méditerranée, le Canal du Midi à l'art contemporain, les lieux aux récits, les publics aux œuvres.</em>
        </p>
      </section>

      {/* Équipe + Vision + Chantier — bloc réutilisable */}
      <div className="px-6 md:px-12 max-w-[1500px] mx-auto">
        <AboutContent />
      </div>

      {/* CTAs */}
      <section className="mt-24 md:mt-32 px-6 md:px-12 max-w-[1500px] mx-auto border-t border-fg/15 pt-12">
        <div className="flex flex-wrap items-center gap-8">
          <Link to="/rejoindre" className="inline-flex items-center gap-3 bg-cinabre text-bg px-8 py-5 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-fg transition-colors group" data-testid="cta-rejoindre">
            Je participe <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/mecenat" className="link-underline font-mono text-[11px] tracking-[0.24em] uppercase" data-testid="cta-mecenat">
            Devenir mécène →
          </Link>
          <Link to="/parcours" className="link-underline font-mono text-[11px] tracking-[0.24em] uppercase" data-testid="cta-parcours">
            Découvrir le parcours →
          </Link>
        </div>
      </section>
    </main>
  );
}
